import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { DocumentPlusIcon, TrashIcon, UsersIcon } from '@heroicons/react/24/outline';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import { transparentize } from 'polished';
import styled from 'styled-components';

import { TenantEntities } from '@/features/canvas/api/tenant-sso';
import { copyGroup } from '@/features/publicLinks/api';
import Button from '@/features/ui/components/Button/Button';
import { useFormContext } from '@/features/ui/components/Form';
import { FormTextInput } from '@/features/ui/components/FormTextInput';

const EditGroup = ({
  item,
  setIsEdit,
  isEdit,
  setIsDeleting,
  setActiveGroup,
  setEntities,
}: {
  isEdit: boolean;
  item: TenantEntities['groups'][number];
  setActiveGroup: Dispatch<SetStateAction<number | null>>;
  setEntities: Dispatch<SetStateAction<TenantEntities>>;
  setIsDeleting: Dispatch<SetStateAction<boolean>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isRecordEdit, setIsRecordEdit] = useState(false);
  const { setValue } = useFormContext();
  const { t } = useTranslation();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);

  const handleEdit = () => {
    if (isEdit) return;

    setIsEdit(true);
    setIsRecordEdit(true);
    setValue('name', item.name);
    setValue('id', item.id);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rowRef.current &&
        !rowRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        (event.target as HTMLButtonElement).id !== `editButton${item.id}`
      ) {
        buttonRef.current.click();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: copyGroupMutation } = useMutation(
    ({ id }: { id: number }) => copyGroup(id),
    {
      onSuccess: (res: TenantEntities['groups'][number]) => {
        setEntities(prev => ({
          ...prev,
          groups: [...prev.groups, res],
        }));
      },
    }
  );

  useEffect(() => {
    if (isRecordEdit) {
      setIsRecordEdit(isEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  return (
    <>
      {isRecordEdit && isEdit ? (
        <TableRow ref={rowRef}>
          <StyledTableCell>
            <StyledFormInput name="name" />
            <Button ref={buttonRef} type="submit" style={{ display: 'none' }} label="" />
          </StyledTableCell>
          <StyledTableCell />
          <StyledTableCell />
          <StyledTableCell />
        </TableRow>
      ) : (
        <TableRow>
          <StyledTableCell>
            <button onClick={handleEdit} type="button" id={`editButton${item.id}`}>
              {item.name}
            </button>
          </StyledTableCell>
          <StyledTableCell>
            <Actions>
              <Tooltip title={t('access.manage_users')} placement="top">
                <IconWrapper>
                  <UsersIcon width={20} height={20} onClick={() => setActiveGroup(item.id)} />
                </IconWrapper>
              </Tooltip>
            </Actions>
          </StyledTableCell>
          <StyledTableCell>
            <Actions>
              <Tooltip title={t('access.duplicate')} placement="top">
                <IconWrapper>
                  <DocumentPlusIcon
                    width={20}
                    height={20}
                    onClick={() => copyGroupMutation({ id: item.id })}
                  />
                </IconWrapper>
              </Tooltip>
            </Actions>
          </StyledTableCell>
          <StyledTableCell>
            <Actions>
              <Tooltip title={t('access.delete')} placement="top">
                <IconWrapper>
                  <TrashIcon
                    width={20}
                    height={20}
                    onClick={() => {
                      setIsDeleting(true);
                      setActiveGroup(item.id);
                    }}
                  />
                </IconWrapper>
              </Tooltip>
            </Actions>
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
};

const StyledFormInput = styled(FormTextInput)`
  padding: 0 0.5rem;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => transparentize(0.7, theme.palette.brand.corePPTCopy)};
  font-size: 2rem;

  &:focus-within {
    border: 1px solid ${({ theme }) => transparentize(0.7, theme.palette.brand.corePPTCopy)};
    background: ${({ theme }) => theme.palette.brand.white};
  }
`;

const StyledTableCell = styled(TableCell)`
  button {
    font-size: 2rem;
    color: ${({ theme }) => theme.palette.brand.textSecondary};
    text-align: left;
  }
`;

const IconWrapper = styled.div`
  cursor: pointer;
  width: 2.2rem;
  height: 2.2rem;

  svg {
    width: 100%;
    height: 100%;
    path,
    rect {
      stroke: ${({ theme }) => theme.palette.brand.primary};
    }
  }

  &:hover {
    svg {
      path,
      rect {
        stroke: ${({ theme }) => theme.palette.brand.editorTabPrimary};
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
`;

export default EditGroup;
